import React, { useState } from 'react';
import { useAuthStore } from "@/store/authStore";
import { CustomersTableView } from "../../verve-contacts/components/CustomersTableView";
import { CONTACT_STAGES } from "../../verve-contacts/constants/contactConstants";
import { useVerveContacts } from "../../verve-contacts/hooks/useVerveContacts";
import { useTelecallers } from "../../verve-contacts/hooks/useTelecallers";
import { ArrowUpRight } from "lucide-react";
import { ContactModals } from "../../verve-contacts/components/ContactModals";
import { useContactActivities } from "../../verve-contacts/hooks/useContactActivities";
import { useClientPagination } from "@/hooks";
import { Pagination, FilterSection } from "@/components/ui";

const ClientsTable = ({ onProceedToPrep, onAddContact }) => {
  const { user } = useAuthStore();
  const [tableParams, setTableParams] = useState({
    status: 'initial_quotation'
  });
  const [detail, setDetail] = useState(null);
  const [actNote, setActNote] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({
    date: "all",
    fromDate: "",
    toDate: ""
  });

  // Update tableParams when filters or search change
  React.useEffect(() => {
    setTableParams({
      status: 'initial_quotation',
      search: search || undefined,
      date: filters.date !== 'all' ? filters.date : undefined,
      fromDate: filters.fromDate || undefined,
      toDate: filters.toDate || undefined
    });
  }, [search, filters]);
  const { activities, addActivity } = useContactActivities(detail?._id);

  const {
    contacts,
    isLoading,
    updateContact
  } = useVerveContacts(tableParams);

  const { telecallers } = useTelecallers();

  const updateStatus = async (customer, targetStatus) => {
    await updateContact({
      id: customer._id,
      data: { status: targetStatus, teleStatus: targetStatus }
    });
  };

  const onCustomerClick = (customer) => {
    setDetail(customer);
  };

  /* 
  const onEdit = (customer) => {
    console.log("Edit customer", customer);
  };

  const onToggleDisable = async (id, currentStatus) => {
    await updateContact({ id, data: { isDisabled: !currentStatus } });
  };
  */

  const filteredContacts = (contacts || []).filter(c => {
    const term = search.toLowerCase();
    const nameMatch = (c.firstName || "").toLowerCase().includes(term) || (c.lastName || "").toLowerCase().includes(term) || (c.name || "").toLowerCase().includes(term);
    const idMatch = (c.customId || "").toLowerCase().includes(term);
    return nameMatch || idMatch;
  });

  const { paginatedData, totalPages } = useClientPagination(filteredContacts, page, 10);

  return (
    <div className="flex-1 flex flex-col gap-3 h-full overflow-hidden">
      <div className="flex flex-col gap-2">
        <h2 className="text-lg font-bold text-slate-800">Initial Quotation Phase Clients</h2>
        <FilterSection
          search={search}
          setSearch={(val) => {
            setSearch(val);
            setPage(1);
          }}
          filters={filters}
          setFilters={(key, val) => {
            if (typeof key === 'object') {
              setFilters(prev => ({ ...prev, ...key }));
            } else {
              setFilters(prev => ({ ...prev, [key]: val }));
            }
            setPage(1);
          }}
          hideViewToggle={true}
          hideFilterButton={true}
          onAddClick={onAddContact}
          addLabel="Contact"
        />
      </div>
      <div className="flex-1 bg-white rounded-xl border border-slate-200 h-full overflow-hidden flex flex-col">
        <div className="flex-1 overflow-y-auto p-0">
          <CustomersTableView
          filtered={paginatedData}
          isLoading={isLoading}
          updateStatus={updateStatus}
          onCustomerClick={onCustomerClick}
          stages={CONTACT_STAGES}
          // onEdit={onEdit}
          // onToggleDisable={onToggleDisable}
          user={user}
          telecallers={telecallers || []}
          onAssignStaff={async (id, staffId) => {
            await updateContact({
              id,
              data: {
                assignedTo: staffId,
                telecaller: staffId,
                status: staffId ? "assigned" : "new"
              }
            });
          }}
          extraActions={(customer) => (
            <div className="w-7 h-7 rounded-full flex items-center justify-center bg-[#f0f2f5] border border-slate-200/50 shadow-[inset_1px_1px_2px_rgba(165,175,190,0.4),_inset_-1px_-1px_2px_rgba(255,255,255,0.9)] shrink-0 ml-1">
              <button
                onClick={() => onProceedToPrep && onProceedToPrep(customer)}
                className="w-5 h-5 rounded-full flex items-center justify-center bg-[#f0f2f5] text-brand-blue border border-white shadow-[1.5px_1.5px_3px_rgba(165,175,190,0.4),_-1.5px_-1.5px_3px_rgba(255,255,255,0.95)] hover:text-blue-700 hover:bg-[#e6e9ef] transition-all duration-300"
                title="Proceed to Quotation Prep"
              >
                <ArrowUpRight className="w-3 h-3" />
              </button>
            </div>
          )}
        />
      </div>
      {totalPages > 1 && (
        <div className="p-4 border-t border-slate-200 bg-white">
          <Pagination
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
          />
        </div>
      )}
      </div>
      {detail && (
        <ContactModals
          detail={detail}
          setDetail={setDetail}
          activities={activities}
          actNote={actNote}
          setActNote={setActNote}
          onAddActivity={async (note) => {
            if (addActivity) {
              const noteStr = typeof note === 'string' ? note : (note?.note || note?.comment || actNote);
              await addActivity({ note: noteStr || actNote });
              setActNote("");
            }
          }}
          updateStatus={updateStatus}
          stages={CONTACT_STAGES}
          user={user}
        />
      )}
    </div>
  );
};

export default ClientsTable;
